"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";

/* Shared first-party data layer per SOP v9.0 §7.2.
 *
 * Pushes a structured page_view event into window.dataLayer on every
 * route change so GTM / Meta / Pinterest / TikTok / X pixels all
 * receive a consistent payload with brand + pageType + niche
 * segmentation. The Pixels component handles the actual pixel fires;
 * this just feeds them a normalized event.
 *
 * Adds ~no perceived weight — runs once per route transition, pushes
 * a tiny object, no listeners. Skips on /admin/* (we don't want
 * marketing analytics to count admin clicks). */
export function DataLayerPush() {
  const pathname = usePathname();

  useEffect(() => {
    if (!pathname || pathname.startsWith("/admin")) return;

    const pageType = derivePageType(pathname);
    const niche = deriveNiche(pathname);
    const productCategory = deriveCategory(pathname);

    const w = window as typeof window & {
      dataLayer?: Array<Record<string, unknown>>;
    };
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({
      event: "page_view",
      brand: "Apex Flow Digital",
      page_path: pathname,
      page_type: pageType,
      niche: niche ?? undefined,
      product_category: productCategory ?? undefined,
    });
  }, [pathname]);

  return null;
}

/* Coarse-grained page type — drives segmentation in GTM/GA4 + pixel
 * retargeting audiences without us needing per-route handlers. */
function derivePageType(pathname: string): string {
  if (pathname === "/") return "home";
  if (pathname.startsWith("/digital-art/")) return "wall_art_pdp";
  if (pathname === "/digital-art") return "wall_art_catalog";
  if (pathname.startsWith("/templates/pack/")) return "template_pdp";
  if (pathname === "/templates") return "template_catalog";
  if (pathname.startsWith("/notion/")) return "notion_pdp";
  if (pathname === "/notion") return "notion_catalog";
  if (pathname.startsWith("/bundles/")) return "bundle_landing";
  if (pathname === "/bundles") return "bundle_catalog";
  if (pathname.startsWith("/n/")) return "niche_hub";
  if (pathname === "/n") return "niche_index";
  if (pathname === "/membership") return "membership_landing";
  if (pathname === "/cart") return "cart";
  if (pathname.startsWith("/order/confirm")) return "order_confirm";
  if (pathname === "/free-1000-assets") return "lead_magnet";
  if (pathname.startsWith("/compare/")) return "compare_pdp";
  if (pathname === "/compare") return "compare_index";
  if (pathname.startsWith("/how-to/")) return "howto_pdp";
  if (pathname === "/how-to") return "howto_index";
  if (pathname.startsWith("/best/")) return "bestof_pdp";
  if (pathname === "/best") return "bestof_index";
  if (pathname.startsWith("/help")) return "help";
  if (pathname.startsWith("/legal")) return "legal";
  if (pathname.startsWith("/about")) return "about";
  if (pathname.startsWith("/auth/")) return "auth";
  if (pathname.startsWith("/account/")) return "account";
  return "other";
}

/* Niche segment for retargeting — pulled from /n/[niche] and from
 * bundle landings + niche-suffixed compare pages. */
function deriveNiche(pathname: string): string | null {
  const niche = pathname.match(/^\/n\/([^/?#]+)/)?.[1];
  if (niche) return niche;
  const bundle = pathname.match(/^\/bundles\/([^/?#]+)/)?.[1];
  if (bundle) return bundle;
  return null;
}

function deriveCategory(pathname: string): string | null {
  if (pathname.startsWith("/digital-art")) return "wall_art";
  if (pathname.startsWith("/templates")) return "templates";
  if (pathname.startsWith("/notion")) return "notion";
  if (pathname.startsWith("/bundles")) return "bundles";
  if (pathname === "/membership") return "insider_pass";
  return null;
}
